import {useMemo} from 'react';
import type {AssayCatalogItem, PartnerTier} from '@/shared/types/api';

export interface Multipliers {
    reagent: number;
    calibrator: number;
    shared: number;
}

export interface PricingSummary {
    testsIncluded: number;
    totalTests: number;
    panelPrice: number;
    cogs: number;
    grossProfit: number;
    marginPercent: number;
    addonTotal: number;
    serumIaUl: number;
    serumChemUl: number;
    iaDeadSpace: number;
    chemDeadSpace: number;
    serumIaTotal: number;
    serumChemTotal: number;
    serumTotal: number;
}

const MARKUP: Record<PartnerTier, number> = {
    payg: 4,
    standard: 2.5,
    preferred: 2,
    enterprise: 2,
};

/** Dead space in microliters (uL) for the immunoassay (DXi) serum tube */
const IA_DEAD_SPACE = 80;
/** Dead space in microliters (uL) for the chemistry (AU) serum tube */
const CHEM_DEAD_SPACE = 55;

function num(val: string | null | undefined): number {
    if (val == null) return 0;
    const n = parseFloat(val);
    return Number.isFinite(n) ? n : 0;
}

export const TIER_DEFAULT_MARKUP: Record<PartnerTier, number> = MARKUP;

export function computeTestPrice(
    assay: AssayCatalogItem,
    multipliers: Multipliers,
    markup: number
): {cost: number; price: number} {
    const reagent = num(assay.reagent_cost) * multipliers.reagent;
    const calibrator = num(assay.calibrator_cost) * multipliers.calibrator;
    const shared = num(assay.shared_cost) * multipliers.shared;
    const cost = reagent + calibrator + shared;
    return {cost: Math.round(cost * 100) / 100, price: Math.round(cost * markup * 100) / 100};
}

export function getAddonPrice(assay: AssayCatalogItem, tier: PartnerTier): number {
    const key =
        tier === 'payg' ? 'payg_addon_price'
        : tier === 'preferred' || tier === 'enterprise' ? 'pref_addon_price'
        : 'std_addon_price'; // standard
    return num(assay[key]);
}

export function usePricingCalculator(
    assays: AssayCatalogItem[],
    includedCodes: Set<string>,
    tier: PartnerTier,
    multipliers: Multipliers,
    markupOverride?: number | null
): PricingSummary {
    return useMemo(() => {
        const markup = markupOverride != null && Number.isFinite(markupOverride) && markupOverride > 0
            ? markupOverride
            : MARKUP[tier];
        let panelPrice = 0;
        let cogs = 0;
        let addonTotal = 0;
        let serumIaUl = 0;
        let serumChemUl = 0;
        let testsIncluded = 0;

        for (const assay of assays) {
            if (!includedCodes.has(assay.code)) continue;
            testsIncluded++;

            const reagent = num(assay.reagent_cost) * multipliers.reagent;
            const calibrator = num(assay.calibrator_cost) * multipliers.calibrator;
            const shared = num(assay.shared_cost) * multipliers.shared;
            const testCost = reagent + calibrator + shared;
            const testPrice = testCost * markup;

            cogs += testCost;
            panelPrice += testPrice;

            addonTotal += getAddonPrice(assay, tier);

            serumIaUl += num(assay.serum_ia_ul);
            serumChemUl += num(assay.serum_chem_ul);
        }

        const grossProfit = panelPrice - cogs;
        const marginPercent = panelPrice > 0 ? (grossProfit / panelPrice) * 100 : 0;

        const hasIa = serumIaUl > 0;
        const hasChem = serumChemUl > 0;
        const iaDeadSpace = hasIa ? IA_DEAD_SPACE : 0;
        const chemDeadSpace = hasChem ? CHEM_DEAD_SPACE : 0;
        const serumIaTotal = serumIaUl + iaDeadSpace;
        const serumChemTotal = serumChemUl + chemDeadSpace;

        return {
            testsIncluded,
            totalTests: assays.length,
            panelPrice: Math.round(panelPrice * 100) / 100,
            cogs: Math.round(cogs * 100) / 100,
            grossProfit: Math.round(grossProfit * 100) / 100,
            marginPercent: Math.round(marginPercent * 10) / 10,
            addonTotal: Math.round(addonTotal * 100) / 100,
            serumIaUl: Math.round(serumIaUl * 100) / 100,
            serumChemUl: Math.round(serumChemUl * 100) / 100,
            iaDeadSpace,
            chemDeadSpace,
            serumIaTotal: Math.round(serumIaTotal * 100) / 100,
            serumChemTotal: Math.round(serumChemTotal * 100) / 100,
            serumTotal: Math.round((serumIaTotal + serumChemTotal) * 100) / 100,
        };
    }, [assays, includedCodes, tier, multipliers, markupOverride]);
}
